import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { OverlayManager } from "../overlay/OverlayManager";
import type { InteractiveLayer } from "../overlay/InteractiveLayer";
import type { CommandHistory } from "../commands/CommandHistory";
import { Grid, type GridTransformListener } from "../transform/Grid";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { getLockedLayerNames } from "../database/layers";
import { readEntityMeta } from "../meta/xdata";
import { keepTopmostEntity } from "./topmost";

/**
 * Above this many entities, a multiple selection shows only its shared box and
 * move handle: one grid per entity would cost more and more on every view
 * change, and hundreds of handles are unusable anyway.
 */
export const MAX_ENTITY_GRIDS = 30;

/**
 * Context for SelectionSet initialization.
 */
export interface SelectionSetContext {
  module: DrawingModuleType;
  db: DrawingModule.OdDbDatabase;
  viewer: DRDrawingViewer;
  overlayManager: OverlayManager;
  interactiveLayer: InteractiveLayer;
  history: CommandHistory;
}

/**
 * SelectionSet - Manages selected entities using native ODA selection.
 *
 * Uses OdDbSelectionSet from DrawingJS for GPU-accelerated selection
 * and OdDbEntity.highlight() for visual feedback.
 * Also manages the transform grid for selected entities.
 */
export class SelectionSet {
  private module: DrawingModuleType | null = null;
  private db: DrawingModule.OdDbDatabase | null = null;
  private viewer: DRDrawingViewer | null = null;
  private history: CommandHistory | null = null;
  private selectionSet: DrawingModule.OdDbSelectionSet | null = null;

  /**
   * Transform grids. One entity: a single grid. Several: the group grid first
   * (shared box + move handle), then one grid per entity with its own handles.
   */
  private grids: Grid[] = [];
  private ctx: SelectionSetContext | null = null;
  /** Touch navigation in progress: grids hidden until it ends. */
  private navigating = false;

  /**
   * Set the context with all required dependencies.
   */
  setContext(ctx: SelectionSetContext): void {
    this.module = ctx.module;
    this.db = ctx.db;
    this.viewer = ctx.viewer;
    this.history = ctx.history;
    this.ctx = ctx;
    // The service outlives the plan: a plan left mid-gesture must not leave
    // the next one with hidden grids.
    this.navigating = false;

    this.grids = [this.createGrid(ctx)];
  }

  private createGrid(ctx: SelectionSetContext): Grid {
    return new Grid(
      ctx.viewer,
      ctx.history,
      ctx.overlayManager,
      ctx.interactiveLayer,
      this.gridListener,
    );
  }

  /**
   * The grids of a multiple selection move together: while one is dragged the
   * others are hidden, and they are all rebuilt once it is committed.
   */
  private readonly gridListener: GridTransformListener = {
    // The dragged grid is transforming: left alone by forEachIdleGrid.
    onTransformStart: () =>
      this.batchOverlays(() => this.forEachIdleGrid((g) => g.hide())),
    onTransformEnd: () => this.updateGrid(),
    onTransformAbort: (dragged) => {
      if (this.navigating) return;
      this.batchOverlays(() =>
        this.forEachIdleGrid((g) => g !== dragged && g.redraw()),
      );
    },
  };

  /**
   * Get the context (viewer and history) for selection operations.
   * Returns null if context has not been set.
   */
  getContext(): { viewer: DRDrawingViewer; history: CommandHistory } | null {
    if (!this.viewer || !this.history) return null;
    return { viewer: this.viewer, history: this.history };
  }

  /**
   * Get the number of selected entities.
   */
  get count(): number {
    if (!this.selectionSet) return 0;
    return this.selectionSet.objectIdArray().size();
  }

  /**
   * Check if selection is empty.
   */
  get isEmpty(): boolean {
    return this.count === 0;
  }

  /**
   * Get the native selection set.
   */
  getNativeSelectionSet(): DrawingModule.OdDbSelectionSet | null {
    return this.selectionSet;
  }

  /**
   * Get all selected entity IDs as an array.
   */
  getAll(): DrawingModule.OdDbObjectId[] {
    if (!this.selectionSet) return [];

    const result: DrawingModule.OdDbObjectId[] = [];
    const idArray = this.selectionSet.objectIdArray();
    for (let i = 0; i < idArray.size(); i++) {
      result.push(idArray.get(i));
    }
    return result;
  }

  /**
   * Get all selected entity handles.
   */
  getAllHandles(): string[] {
    if (!this.selectionSet || !this.module) return [];

    const handles: string[] = [];
    const idArray = this.selectionSet.objectIdArray();

    for (let i = 0; i < idArray.size(); i++) {
      const entityId = idArray.get(i);
      const handle = entityId.getHandle();
      const handleOdStr = handle.ascii();
      handles.push(handleOdStr.toString());
      deleteObj(handleOdStr);
      deleteObj(handle);
    }

    return handles;
  }

  /**
   * Set selection from a native OdDbSelectionSet.
   * Dehighlights previous selection and highlights new one.
   * Filters out non-selectable entities (e.g., hatches).
   * @param topmostOnly Keep only the topmost selectable entity (single click).
   */
  setFromNative(
    newSelectionSet: DrawingModule.OdDbSelectionSet | null,
    topmostOnly = false,
  ): void {
    // Dehighlight old selection
    this.highlightAll(false);

    // Filter out non-selectable entities
    deleteObj(this.selectionSet);
    this.selectionSet = this.filterSelectable(newSelectionSet, topmostOnly);

    // Highlight new selection
    this.highlightAll(true);

    // Update transform grid
    this.updateGrid();
  }

  /**
   * Add a single entity to the selection.
   * @param entityId The entity to add
   * @param updateGrid Whether to update the transform grid (default: true)
   */
  add(entityId: DrawingModule.OdDbObjectId, updateGrid: boolean = true): void {
    if (!this.module || !this.db) return;

    // Create selection set if needed
    if (!this.selectionSet) {
      this.selectionSet = this.module.OdDbSelectionSet.createObject1(this.db);
    }

    // Add entity directly to the internal ID array (bypasses OdDbSelectionMethod requirement)
    const newSelection = this.module.OdDbSelectionSet.createObject1(this.db);
    const method = newSelection.method(entityId);
    newSelection.append(entityId, method);

    // There is a problem with the typing of that method. They forgot 'Db'
    this.selectionSet.append3(newSelection);

    deleteObj(newSelection);
    if (updateGrid) {
      this.updateGrid();
    }
  }

  /**
   * Add entities from another selection set to current selection.
   * Filters out non-selectable entities (e.g., hatches).
   * @param topmostOnly Keep only the topmost selectable entity (single click).
   */
  addFromNative(
    additionalSet: DrawingModule.OdDbSelectionSet,
    topmostOnly = false,
  ): void {
    if (!this.selectionSet || !this.module || !this.db) {
      this.setFromNative(additionalSet, topmostOnly);
      return;
    }

    // Filter out non-selectable entities before appending
    const filteredSet = this.filterSelectable(additionalSet, topmostOnly);
    if (filteredSet) {
      // Append to existing selection (cast to base type)
      this.selectionSet.append3(
        filteredSet as unknown as DrawingModule.OdSelectionSet,
      );
      // append3() copies entries into this.selectionSet, it does not take
      // ownership — the merged-in set must still be released here.
      deleteObj(filteredSet);
    }

    // Highlight all (including new ones)
    this.highlightAll(true);

    // Update transform grid
    this.updateGrid();
  }

  /**
   * Clear all selections.
   */
  clear(): void {
    this.highlightAll(false);
    deleteObj(this.selectionSet);
    this.selectionSet = null;

    // Clear all transform grids
    this.grids.forEach((g) => g.clear());
  }

  /**
   * Highlight or dehighlight all entities in selection.
   */
  highlightAll(doHighlight: boolean): void {
    if (!this.selectionSet || !this.module) return;
    // Selection changed mid-gesture: highlighted when the navigation ends.
    if (doHighlight && this.navigating) return;

    const idArray = this.selectionSet.objectIdArray();

    for (let i = 0; i < idArray.size(); i++) {
      const entityId = idArray.get(i);
      let pObj: DrawingModule.OdDbObject | null = null;
      let entity: DrawingModule.OdDbEntity | null = null;
      try {
        pObj = entityId.openObject1(OpenMode.kForWrite, false);
        entity = this.module.OdDbEntity.cast(pObj);
        if (entity) {
          entity.highlight(doHighlight, null as any, true);
        }
      } catch (e) {
        console.warn(
          `[SelectionSet] highlightAll(${doHighlight}): error for entity ${i}`,
          e,
        );
      } finally {
        if (entity) deleteObj(entity);
        if (pObj) deleteObj(pObj);
      }
    }
  }

  /**
   * Check if an entity is in the selection.
   */
  has(entityId: DrawingModule.OdDbObjectId): boolean {
    if (!this.selectionSet) return false;

    // Get the target handle string, cleaning up intermediate native objects
    const targetHandle = entityId.getHandle();
    const targetAscii = targetHandle.ascii();
    const targetStr = targetAscii.toString();
    deleteObj(targetAscii);
    deleteObj(targetHandle);

    const idArray = this.selectionSet.objectIdArray();
    for (let i = 0; i < idArray.size(); i++) {
      const handle = idArray.get(i).getHandle();
      const ascii = handle.ascii();
      const str = ascii.toString();
      deleteObj(ascii);
      deleteObj(handle);
      if (str === targetStr) {
        return true;
      }
    }
    return false;
  }

  /**
   * Remove an entity from selection.
   * !!! entityId must be deleted by caller.
   */
  remove(entityId: DrawingModule.OdDbObjectId): void {
    if (!this.selectionSet || !this.module) return;

    // Dehighlight the entity first
    let pObj: DrawingModule.OdDbObject | null = null;
    let entity: DrawingModule.OdDbEntity | null = null;
    try {
      pObj = entityId.openObject1(OpenMode.kForWrite, false);
      entity = this.module.OdDbEntity.cast(pObj);
      if (entity) {
        entity.highlight(false, null as any, true);
      }
    } catch (e) {
      console.warn("[SelectionSet] remove: highlight error", e);
    } finally {
      if (entity) deleteObj(entity);
      if (pObj) deleteObj(pObj);
    }

    // Remove from selection set
    this.selectionSet.remove(entityId);

    // Update transform grid
    this.updateGrid();
  }

  // ==================== Transform Grid Methods ====================

  /**
   * Ensure exactly `count` grids exist, creating or trimming as needed.
   */
  private _ensureGridCount(count: number): void {
    while (this.grids.length > count) {
      this.grids.pop()!.clear();
    }
    while (this.grids.length < count && this.ctx) {
      this.grids.push(this.createGrid(this.ctx));
    }
  }

  /**
   * Update the transform grid(s) based on the current selection.
   */
  updateGrid(): void {
    if (this.grids.length === 0) return;
    this.batchOverlays(() => {
      this.rebuildGrids();
      // Rebuilt mid-gesture (undo, selection from a panel…): hidden until
      // the navigation ends, like the others.
      if (this.navigating) this.forEachIdleGrid((g) => g.hide());
    });
  }

  private rebuildGrids(): void {
    const entityIds = this.getAll();
    if (entityIds.length === 0) {
      this.grids.forEach((g) => g.clear());
      return;
    }

    if (entityIds.length === 1) {
      this._ensureGridCount(1);
      this.grids[0].setEntities(entityIds, "entity");
      return;
    }

    const withMembers = entityIds.length <= MAX_ENTITY_GRIDS;
    this._ensureGridCount(withMembers ? entityIds.length + 1 : 1);
    this.grids[0].setEntities(entityIds, "group");
    if (withMembers) {
      entityIds.forEach((id, i) =>
        this.grids[i + 1].setEntities([id], "member"),
      );
    }
  }

  /**
   * Check if any transform operation is in progress.
   */
  get isTransforming(): boolean {
    return this.grids.some((g) => g.isTransforming);
  }

  /**
   * Cancel any active transform operation.
   */
  cancelTransform(): void {
    this.grids.forEach((g) => {
      if (g.isTransforming) g.cancelTransform();
    });
  }

  /**
   * Redraw all transform grids (call after view changes). Skipped during a
   * touch navigation: the grids are hidden until it ends (see setNavigating).
   */
  redrawGrid(): void {
    if (this.navigating) return;
    this.batchOverlays(() => this.grids.forEach((g) => g.redraw()));
  }

  /**
   * Touch navigation (pinch, pan): the selection is not redisplayed while
   * the view moves, two costs that grow with the number of selected
   * entities:
   * - the highlight, redrawn by ODA on every render of the gesture;
   * - the grids, rebuilt on every view change (one per entity in individual
   *   mode).
   * Both come back once, at the new zoom level, when the navigation ends.
   */
  setNavigating(navigating: boolean): void {
    if (this.navigating === navigating) return;
    // A transform in progress keeps its highlight and handles (the drag
    // relies on them); a navigation cannot start during one anyway.
    if (navigating && this.isTransforming) return;
    this.navigating = navigating;
    this.highlightAll(!navigating);
    this.batchOverlays(() =>
      this.forEachIdleGrid((g) => (navigating ? g.hide() : g.redraw())),
    );
    // The gesture's last render had no highlight.
    if (!navigating && !this.isEmpty) this.viewer?.update("selection");
  }

  /** Grids not being dragged (a drag relies on its handles). */
  private forEachIdleGrid(apply: (grid: Grid) => void): void {
    for (const grid of this.grids) if (!grid.isTransforming) apply(grid);
  }

  /** One overlay repaint for all the grids, instead of one per handle. */
  private batchOverlays(mutate: () => void): void {
    if (this.ctx) this.ctx.overlayManager.batch(mutate);
    else mutate();
  }

  /**
   * Get the primary transform grid (kept for API compatibility).
   */
  getGrid(): Grid | null {
    return this.grids[0] ?? null;
  }

  /**
   * Get the layer names of all selected entities.
   * Returns unique layer names.
   */
  getSelectedLayers(): string[] {
    if (!this.selectionSet || !this.module) return [];

    const layerSet = new Set<string>();
    const idArray = this.selectionSet.objectIdArray();

    for (let i = 0; i < idArray.size(); i++) {
      const entityId = idArray.get(i);
      let pObj: DrawingModule.OdDbObject | null = null;
      let entity: DrawingModule.OdDbEntity | null = null;
      try {
        pObj = entityId.openObject1(OpenMode.kForRead, false);
        entity = this.module.OdDbEntity.cast(pObj);
        if (entity) {
          const layerOdStr = entity.layer();
          layerSet.add(layerOdStr.toString());
          deleteObj(layerOdStr);
        }
      } catch (e) {
        console.warn(
          `[SelectionSet] getSelectedLayers: error for entity ${i}`,
          e,
        );
      } finally {
        if (entity) deleteObj(entity);
        if (pObj) deleteObj(pObj);
      }
    }

    return Array.from(layerSet);
  }

  /**
   * Filter out non-selectable entities, then optionally keep only the topmost
   * one — after filtering, so a hidden hatch on top never swallows the click.
   */
  private filterSelectable(
    selectionSet: DrawingModule.OdDbSelectionSet | null,
    topmostOnly: boolean,
  ): DrawingModule.OdDbSelectionSet | null {
    const filtered = this.filterSelectionSet(selectionSet);
    if (!topmostOnly || !this.module || !this.db) return filtered;
    return keepTopmostEntity(this.module, this.db, filtered);
  }

  /**
   * Filter a selection set to remove non-selectable entities.
   * Filters out DR-created OdDbHatch entities AND entities on locked layers.
   *
   * DR only ever creates hatches reactor-linked to a boundary entity (see
   * HatchReactorManager) — the user is meant to select/move that boundary instead,
   * so those hatches are hidden from direct selection. Hatches already present in
   * the source DWG have no such boundary and carry no DR_META (or DR_META with
   * background=true), so they must stay selectable — otherwise they'd be
   * permanently unreachable for the user.
   */
  private filterSelectionSet(
    selectionSet: DrawingModule.OdDbSelectionSet | null,
  ): DrawingModule.OdDbSelectionSet | null {
    const module = this.module;
    const db = this.db;
    if (!selectionSet || !module || !db) return selectionSet;

    const idArray = selectionSet.objectIdArray();
    const hatchDesc = module.OdDbHatch.desc();
    const lockedLayers = getLockedLayerNames(module, db);

    const isFiltered = (entityId: DrawingModule.OdDbObjectId): boolean => {
      let pObj: DrawingModule.OdDbObject | null = null;
      let ent: DrawingModule.OdDbEntity | null = null;
      try {
        pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
        const isHatch = pObj.isA().isEqualTo(hatchDesc);
        const meta = isHatch ? readEntityMeta(module, entityId) : null;
        let drop = isHatch && meta !== null && !meta.background;
        if (!drop && lockedLayers.size) {
          ent = module.OdDbEntity.cast(pObj);
          if (ent) {
            const layerStr = ent.layer();
            if (lockedLayers.has(layerStr.toString())) drop = true;
            deleteObj(layerStr);
          }
        }
        return drop;
      } finally {
        if (ent) deleteObj(ent);
        if (pObj) deleteObj(pObj);
      }
    };

    // First pass: anything to filter out?
    let needsFilter = false;
    for (let i = 0; i < idArray.size(); i++) {
      if (isFiltered(idArray.get(i))) {
        needsFilter = true;
        break;
      }
    }
    if (!needsFilter) return selectionSet;

    // Second pass: build filtered selection set.
    const filteredSet = module.OdDbSelectionSet.createObject1(db);
    for (let i = 0; i < idArray.size(); i++) {
      const entityId = idArray.get(i);
      if (!isFiltered(entityId)) {
        const method = selectionSet.method(entityId);
        filteredSet.append(entityId, method);
      }
    }

    // The original set is now superseded by filteredSet — release it.
    deleteObj(selectionSet);

    if (filteredSet.objectIdArray().size() > 0) {
      return filteredSet;
    }
    deleteObj(filteredSet);
    return null;
  }

  /**
   * Dispose of the selection set and transform grids.
   */
  dispose(): void {
    this.clear();
    this.grids = [];
  }
}

// Re-export types for convenience
export interface EntityBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface SelectedEntityInfo {
  id: DrawingModule.OdDbObjectId;
  handle: string;
}
